import { ActionIcon, Box, Button, Card, CopyButton, Group, Modal, NumberInput, SegmentedControl, Select, Stack, Text, Textarea, TextInput, Title, Tooltip } from "@mantine/core";
import { type CSSProperties, useEffect, useState } from "react";
import { INSTANT_PICKER_WIDTH, InstantPicker } from "../../common/instant-picker";
import { QrCode, qrModules } from "../../common/qr";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { UtilityTitle } from "../../common/utility-title";
import { IconCheck, IconCopy, IconQrcode, IconRefresh } from "../../icons";
import { computeCode } from "./compute";
import type { Algorithm } from "./hotp";
import { DEFAULT_SUITE, parseSuite, questionNoun, questionProblem, sessionProblem, type Suite } from "./ocra";
import { generateSecret, pickSecretFormat, SECRET_FORMATS, type SecretFormat, secretProblem } from "./secret";
import { ALGORITHM_OPTIONS, clampWhole, COUNTER_RANGE, DIGIT_RANGE, hashLabel, MAX_COUNTER, MAX_TIME, type Mode, MODE_OPTIONS, MODES, parseWhole, PERIOD_RANGE, pickAlgorithm, pickMode, pickText, pickTextOr, SECRET_SIZES, TIME_RANGE } from "./settings";
import { DEFAULT_ISSUER, DEFAULT_LABEL, readUri, type UriFields, uriKeyless, writeUri } from "./uri";

export default function Otp() {
  const initialState = useInitialHashState<{
    mode?: string;
    issuer?: string;
    label?: string;
    secret?: string;
    format?: string;
    algorithm?: string;
    digits?: number;
    period?: number;
    counter?: number;
    time?: number;
    suite?: string;
    question?: string;
    password?: string;
    session?: string;
  }>();

  const [mode, setMode] = useState<Mode>(pickMode(initialState?.mode));
  const [issuer, setIssuer] = useState(pickTextOr(initialState?.issuer, DEFAULT_ISSUER));
  const [label, setLabel] = useState(pickTextOr(initialState?.label, DEFAULT_LABEL));
  const [secret, setSecret] = useState(pickText(initialState?.secret));
  const [format, setFormat] = useState<SecretFormat>(pickSecretFormat(initialState?.format));
  const [algorithm, setAlgorithm] = useState<Algorithm>(pickAlgorithm(initialState?.algorithm));
  const [digits, setDigits] = useState<number | string>(clampWhole(initialState?.digits, DIGIT_RANGE, 6));
  const [period, setPeriod] = useState<number | string>(clampWhole(initialState?.period, PERIOD_RANGE, 30));
  const [counter, setCounter] = useState<number | string>(clampWhole(initialState?.counter, COUNTER_RANGE, 0));
  // Blank is the clock rather than a moment, so this one is left as it was typed instead of clamped onto a default.
  const [time, setTime] = useState<number | string>(typeof initialState?.time === "number" ? initialState.time : "");
  const [suiteText, setSuiteText] = useState(initialState?.suite ? pickText(initialState.suite) : DEFAULT_SUITE);
  const [question, setQuestion] = useState(pickText(initialState?.question));
  const [password, setPassword] = useState(pickText(initialState?.password));
  const [session, setSession] = useState(pickText(initialState?.session));
  const [now, setNow] = useState(() => Date.now());
  // What is being typed into the URI box, beside the URI the fields spelled when it was typed against them.
  const [uriDraft, setUriDraft] = useState<UriDraft | null>(null);
  const [scanning, setScanning] = useState(false);
  // Whether a code has been asked for yet, which is what turns a field nobody has filled in into a missing one.
  const [asked, setAsked] = useState(false);

  // Only OCRA has a suite, and the other two modes tick once a second: a parse neither of them reads is one to skip.
  const reading = mode === "ocra" ? readSuite(suiteText) : NO_SUITE;
  const suite = reading.suite;
  const live = time === "";
  const parsedTime = live ? null : parseWhole(time, TIME_RANGE);
  const seconds = live ? Math.floor(now / 1000) : parsedTime;
  // The calendar opens on the second the page is reading, and on today where what was typed is not one at all.
  const instant = seconds === null ? null : new Date(seconds * 1000);
  // The step a code sits in, which is TOTP's period and whatever G the suite named: both are counted the same way.
  const stepSeconds = mode === "totp" ? parseWhole(period, PERIOD_RANGE) ?? 0 : suite?.step ?? 0;

  // Only while the clock is what is being read: a moment somebody typed is not one that goes stale a second later.
  // Each tick is scheduled onto the next whole second rather than a second from the last one, because a step ends on
  // a whole second and an interval that drifts off one leaves the seconds beside the bar turning over after it does.
  useEffect(() => {
    if (!live || stepSeconds === 0) return;
    let timer = 0 as unknown as ReturnType<typeof setTimeout>;
    const tick = () => {
      setNow(Date.now());
      timer = setTimeout(tick, 1000 - (Date.now() % 1000));
    };
    timer = setTimeout(tick, 1000 - (Date.now() % 1000));
    return () => clearTimeout(timer);
  }, [live, stepSeconds]);

  // The secret is an input rather than an answer, so the link carries it as /jwt carries a signing key: a link that
  // left it out would open on the settings and no code at all, which is the one thing the reader came for. Nothing
  // in a fragment is sent to a server, and everything else is only sent while the mode on screen is asking for it.
  useRegisterShareState(() => ({
    mode,
    // Sent even where they are empty, the default of each being a word rather than nothing: a link that left a
    // cleared box out would open on "utils.plus" again, which is not the URI the reader was looking at.
    issuer: mode === "ocra" ? undefined : issuer,
    label: mode === "ocra" ? undefined : label,
    secret: secret || undefined,
    format,
    algorithm: mode === "ocra" ? undefined : algorithm,
    digits: mode === "ocra" ? undefined : digits,
    period: mode === "totp" ? period : undefined,
    counter: mode === "hotp" || suite?.counter ? counter : undefined,
    time: stepSeconds > 0 && !live ? time : undefined,
    suite: mode === "ocra" ? suiteText : undefined,
    question: suite ? question || undefined : undefined,
    password: suite?.password ? password || undefined : undefined,
    session: suite?.session ? session || undefined : undefined,
  }));

  const secretIssue = secretProblem(secret, format);
  // An empty box is unfinished rather than wrong, so it waits for the button; a half-typed secret is wrong at once.
  const secretError = secretIssue ?? (asked && !secret ? "Required" : null);
  const digitsError = parseWhole(digits, DIGIT_RANGE) === null
    ? `Enter a digit count between ${DIGIT_RANGE.min} and ${DIGIT_RANGE.max}`
    : null;
  const periodError = parseWhole(period, PERIOD_RANGE) === null
    ? `Enter a period between ${PERIOD_RANGE.min} and ${PERIOD_RANGE.max} seconds`
    : null;
  const counterError = parseWhole(counter, COUNTER_RANGE) === null ? "Enter a whole counter value" : null;
  const timeError = live || parsedTime !== null ? null : "Enter a time in epoch seconds";
  const questionIssue = suite ? questionProblem(question, suite) : null;
  const questionError = questionIssue ?? (asked && suite && !question ? "Required" : null);
  const sessionError = suite ? sessionProblem(session, suite) : null;

  const result = computeCode({
    mode,
    secret,
    format,
    algorithm,
    digits: parseWhole(digits, DIGIT_RANGE),
    period: parseWhole(period, PERIOD_RANGE),
    counter: parseWhole(counter, COUNTER_RANGE),
    seconds,
    suite,
    question,
    password,
    session,
  });

  // The bar is the clock running out, so it is only drawn while the clock is what the code is being counted from.
  const remaining = live && stepSeconds > 0 && seconds !== null ? stepSeconds - (seconds % stepSeconds) : null;

  // Everything the Key Uri Format has a place for. OCRA is spelled as a TOTP it will never be shown as, since the
  // box is off in that mode: one expression that always reads is less to go wrong than a branch around all of it.
  const uriFields: UriFields = {
    mode: mode === "hotp" ? "hotp" : "totp",
    issuer,
    label,
    secret,
    format,
    algorithm,
    digits: parseWhole(digits, DIGIT_RANGE),
    period: parseWhole(period, PERIOD_RANGE),
    counter: parseWhole(counter, COUNTER_RANGE),
  };
  const uri = writeUri(uriFields);
  // The box shows what is being typed into it for as long as the fields still spell the URI it was typed against,
  // and theirs again the moment one of them says something the draft did not. Nothing is kept in two places: the
  // draft is the half-finished text alone, and every field it names has already been written up to the boxes.
  const showingDraft = uriDraft !== null && uriDraft.against === uri;
  const uriText = showingDraft ? uriDraft.text : uri;
  const uriError = showingDraft ? uriDraft.error : null;

  // The new mode's fields are ones nobody has reached yet, whatever was asked of the last one.
  const changeMode = (value: string) => {
    setMode(pickMode(value));
    setAsked(false);
  };

  // The URI and the boxes above it are one state written twice, so typing into either is a change to the other.
  // The draft is anchored to the URI the fields will spell once this has been applied, which is what lets a
  // parameter be emptied and typed afresh: the boxes keep the last thing that read, and the text stands as typed.
  const editUri = (text: string) => {
    const next = readUri(text, uriFields);
    if (next) applyUri(next);
    setUriDraft({
      text,
      against: next ? writeUri(next) : uri,
      error: next ? null : "A URI here opens otpauth://totp/ or otpauth://hotp/",
    });
  };

  const applyUri = (next: UriFields) => {
    // A URI of the other kind is a mode change, and its fields are ones nobody has been asked for yet either.
    if (next.mode !== mode) setAsked(false);
    setMode(next.mode);
    setIssuer(next.issuer);
    setLabel(next.label);
    setSecret(next.secret);
    setFormat(next.format);
    setAlgorithm(next.algorithm);
    // Null is a box this URI says nothing about, which keeps what it was holding rather than being emptied.
    if (next.digits !== null) setDigits(next.digits);
    if (next.period !== null) setPeriod(next.period);
    if (next.counter !== null) setCounter(next.counter);
  };

  // Sized to the hash it is about to be keyed into, which for OCRA is the one its suite names rather than the picker.
  const generate = () => setSecret(generateSecret(format, SECRET_SIZES[suite?.algorithm ?? algorithm]));

  // One code per use is the whole of HOTP, so the step the token would take is a button rather than two clicks of a
  // spinner. A counter with nowhere left to go stays where it is, the field stopping where whole numbers do.
  const nextCode = () => setCounter((current) => Math.min((parseWhole(current, COUNTER_RANGE) ?? -1) + 1, MAX_COUNTER));

  return (
    <Stack gap="md">
      <UtilityTitle
        directory="otp"
        control={
          <SegmentedControl
            value={mode}
            onChange={changeMode}
            aria-label="Which one-time password"
            data={MODE_OPTIONS}
          />
        }
      >
        {MODES[mode].title}
      </UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Stack>
          <Box className={secretError ? "settings-row has-error" : "settings-row"} mb={secretError ? "md" : 0}>
            <TextInput
              label="Secret"
              description="The key both sides share, which never leaves this tab"
              placeholder="e.g. JBSWY3DPEHPK3PXP"
              value={secret}
              onChange={(event) => setSecret(event.currentTarget.value)}
              error={secretError}
              spellCheck={false}
              classNames={{ root: "relative-root", error: "absolute-error" }}
              styles={{ input: { fontFamily: "monospace" } }}
              rightSectionPointerEvents="all"
              rightSection={
                <Tooltip label="Generate a secret" withArrow position="left">
                  <ActionIcon
                    color="gray"
                    variant="subtle"
                    onClick={generate}
                    aria-label="Generate a secret"
                  >
                    <IconRefresh size="1.1rem" />
                  </ActionIcon>
                </Tooltip>
              }
            />
            <Select
              label="Secret Format"
              data={SECRET_FORMATS}
              value={format}
              onChange={(value) => setFormat(pickSecretFormat(value))}
              allowDeselect={false}
            />
          </Box>

          {mode !== "ocra" && (
            <Box className="settings-row">
              <TextInput
                label="Issuer"
                description="Who the code is for, which is what an authenticator files it under"
                value={issuer}
                onChange={(event) => setIssuer(event.currentTarget.value)}
                spellCheck={false}
              />
              <TextInput
                label="Label"
                description="Which account it is, beside the issuer in that same list"
                value={label}
                onChange={(event) => setLabel(event.currentTarget.value)}
                spellCheck={false}
              />
            </Box>
          )}

          {mode !== "ocra" && (
            <Box
              className={digitsError || counterError ? "settings-row has-error" : "settings-row"}
              mb={digitsError || counterError ? "md" : 0}
            >
              <Select
                label="Algorithm"
                data={ALGORITHM_OPTIONS}
                value={algorithm}
                onChange={(value) => setAlgorithm(pickAlgorithm(value))}
                allowDeselect={false}
              />
              <NumberInput
                label="Digits"
                value={digits}
                onChange={setDigits}
                min={DIGIT_RANGE.min}
                max={DIGIT_RANGE.max}
                allowDecimal={false}
                allowNegative={false}
                error={digitsError}
                classNames={{ root: "relative-root", error: "absolute-error" }}
              />
              {mode === "hotp" && (
                <NumberInput
                  label="Counter"
                  description="Stepped once per code the token hands out"
                  value={counter}
                  onChange={setCounter}
                  min={0}
                  max={MAX_COUNTER}
                  allowDecimal={false}
                  allowNegative={false}
                  stepHoldDelay={500}
                  stepHoldInterval={(t) => Math.max(1000 / t ** 2, 75)}
                  error={counterError}
                  classNames={{ root: "relative-root", error: "absolute-error" }}
                />
              )}
            </Box>
          )}

          {mode === "totp" && (
            <Box
              className={periodError || timeError ? "settings-row has-error" : "settings-row"}
              mb={periodError || timeError ? "md" : 0}
            >
              <NumberInput
                label="Period"
                description="Seconds each code is good for"
                value={period}
                onChange={setPeriod}
                min={PERIOD_RANGE.min}
                max={PERIOD_RANGE.max}
                allowDecimal={false}
                allowNegative={false}
                error={periodError}
                classNames={{ root: "relative-root", error: "absolute-error" }}
              />
              <TimeInput value={time} onChange={setTime} error={timeError} instant={instant} live={live} />
            </Box>
          )}

          {mode === "ocra" && (
            <TextInput
              label="Suite"
              description="What the two sides agreed to compute, and part of the message itself"
              placeholder={DEFAULT_SUITE}
              value={suiteText}
              onChange={(event) => setSuiteText(event.currentTarget.value)}
              error={reading.error}
              spellCheck={false}
              styles={{ input: { fontFamily: "monospace" } }}
            />
          )}

          {suite && (
            <>
              <Box
                className={questionError || counterError ? "settings-row has-error" : "settings-row"}
                mb={questionError || counterError ? "md" : 0}
              >
                <TextInput
                  label="Question"
                  description={`The challenge, up to ${suite.question.length} ${questionNoun(suite.question.format)}`}
                  value={question}
                  onChange={(event) => setQuestion(event.currentTarget.value)}
                  error={questionError}
                  spellCheck={false}
                  classNames={{ root: "relative-root", error: "absolute-error" }}
                  styles={{ input: { fontFamily: "monospace" } }}
                />
                {suite.counter && (
                  <NumberInput
                    label="Counter"
                    description="Stepped once per code the token hands out"
                    value={counter}
                    onChange={setCounter}
                    min={0}
                    max={MAX_COUNTER}
                    allowDecimal={false}
                    allowNegative={false}
                    stepHoldDelay={500}
                    stepHoldInterval={(t) => Math.max(1000 / t ** 2, 75)}
                    error={counterError}
                    classNames={{ root: "relative-root", error: "absolute-error" }}
                  />
                )}
              </Box>

              {(suite.password || suite.session !== 0) && (
                <Box
                  className={sessionError ? "settings-row has-error" : "settings-row"}
                  mb={sessionError ? "md" : 0}
                >
                  {suite.password && (
                    <TextInput
                      label="PIN or password"
                      description={`Hashed with ${hashLabel(suite.password)} before it is counted, as the suite asks`}
                      value={password}
                      onChange={(event) => setPassword(event.currentTarget.value)}
                      spellCheck={false}
                    />
                  )}
                  {suite.session !== 0 && (
                    <TextInput
                      label="Session information"
                      description={`What this exchange is about, up to ${suite.session} bytes`}
                      value={session}
                      onChange={(event) => setSession(event.currentTarget.value)}
                      error={sessionError}
                      spellCheck={false}
                      classNames={{ root: "relative-root", error: "absolute-error" }}
                    />
                  )}
                </Box>
              )}

              {suite.step !== 0 && (
                <Box className={timeError ? "settings-row has-error" : "settings-row"} mb={timeError ? "md" : 0}>
                  <TimeInput value={time} onChange={setTime} error={timeError} instant={instant} live={live} />
                </Box>
              )}
            </>
          )}

          {mode !== "ocra" && (
            <Textarea
              label="URI"
              description="What an authenticator is enrolled with, which is the fields above and writes them back"
              value={uriText}
              onChange={(event) => editUri(event.currentTarget.value)}
              error={uriError}
              autosize
              minRows={2}
              maxRows={4}
              spellCheck={false}
              styles={{ input: { fontFamily: "monospace" } }}
              // The two icons sit at the top of a box that grows downwards, which is where they are when it is one
              // line tall and where the eye is left after reading across it.
              rightSectionWidth={URI_ACTIONS_WIDTH}
              rightSectionProps={{ style: { alignItems: "flex-start", paddingTop: "var(--mantine-spacing-xs)" } }}
              rightSectionPointerEvents="all"
              rightSection={
                <Group gap={2} wrap="nowrap">
                  <CopyButton value={uriText} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied" : "Copy the URI"} withArrow position="left">
                        <ActionIcon
                          color={copied ? "teal" : "gray"}
                          variant="subtle"
                          onClick={copy}
                          aria-label="Copy the URI"
                        >
                          {copied ? <IconCheck size="1.1rem" /> : <IconCopy size="1.1rem" />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                  <Tooltip label="Show the QR code" withArrow position="left">
                    <ActionIcon
                      color="gray"
                      variant="subtle"
                      onClick={() => setScanning(true)}
                      aria-label="Show the QR code"
                    >
                      <IconQrcode size="1.1rem" />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              }
            />
          )}

          <Group justify="flex-end">
            {mode === "hotp" && <Button variant="default" onClick={nextCode}>Next</Button>}
            {/* Never disabled: clicking it with a field still blank is how the page says which one. */}
            <Button onClick={() => setAsked(true)}>Calculate</Button>
          </Group>
        </Stack>
      </Card>

      {result && (
        <Card withBorder shadow="sm" radius="md">
          <Stack>
            <Group gap="sm" align="baseline">
              <Title order={4}>Code</Title>
              <Text size="sm" c="dimmed">{result.crypto}</Text>
            </Group>

            {/* Beside what it copies rather than up in the heading, and at the top of a code that is 128 hex long. */}
            <Group gap="sm" align="flex-start" wrap="nowrap">
              {/* An untruncated suite answers with the whole HMAC, which is a value to compare and not one to read out. */}
              <Text className="otp-code" data-mac={result.code.length > DIGIT_RANGE.max || undefined} miw={0}>
                {result.code}
              </Text>
              <CopyButton value={result.code} timeout={2000}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="top">
                    <ActionIcon
                      color={copied ? "teal" : "gray"}
                      variant="subtle"
                      onClick={copy}
                      aria-label="Copy the code"
                    >
                      {copied ? <IconCheck size="1.2rem" /> : <IconCopy size="1.2rem" />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>

            {remaining !== null && (
              <Stack gap={4}>
                {/* Keyed on the period, which is the one thing that makes an anchor already taken the wrong one. */}
                <Countdown key={stepSeconds} period={stepSeconds} remaining={remaining} />
                <Text size="sm" c="dimmed">
                  Good for another {remaining} {remaining === 1 ? "second" : "seconds"}
                </Text>
              </Stack>
            )}

            {result.counted.length > 0 && (
              <Group gap="lg">
                {result.counted.map((item) => (
                  <Text key={item.label} size="sm" c="dimmed">
                    {item.label} <Text span ff="monospace" c="dimmed">{item.value}</Text>
                  </Text>
                ))}
              </Group>
            )}
          </Stack>
        </Card>
      )}

      {/* Nothing is encoded until it is asked for, a code being redrawn on every keystroke otherwise. */}
      <Modal opened={scanning} onClose={() => setScanning(false)} title="Scan to enrol" centered>
        <QrPanel uri={uriText} />
      </Modal>
    </Stack>
  );
}

// The URI as a camera reads it, drawn by the same component /qr-code draws its own with.
function QrPanel({ uri }: QrPanelProps) {
  const modules = qrModules(uri);

  return (
    <Stack gap="md" align="center">
      {modules === null
        ? (
          <Text size="sm" c="dimmed" ta="center">
            This URI is longer than any QR code has room for, so there is nothing here a camera could read.
          </Text>
        )
        : <QrCode modules={modules} label="QR code for the URI" />}

      {/* What is being scanned, since a code nobody can read is the one thing a QR code is. */}
      <Text size="xs" c="dimmed" ff="monospace" style={{ wordBreak: "break-all" }}>{uri}</Text>

      {uriKeyless(uri) && (
        <Text size="sm" c="dimmed" ta="center">
          There is no secret in this URI, so what it enrols is an account with no key behind it.
        </Text>
      )}
    </Stack>
  );
}

interface QrPanelProps {
  uri: string;
}

// The seconds a code has left. The sweep is anchored where the step already is and then left alone: the delay is
// read once, in a `useState` initialiser that runs on mount and never again, because `animation-delay` is measured
// from the animation's own start and not from now — recomputing it each second would advance the bar by that second
// on top of the one that had actually passed, which is a bar at twice the speed lurching forward on every tick.
// Left alone it settles on `time mod period` for good, so every wrap after the first lands on a step boundary.
function Countdown({ period, remaining }: CountdownProps) {
  const [elapsed] = useState(() => (Date.now() / 1000) % period);

  return (
    <Box
      className="otp-countdown"
      data-low={remaining <= LOW_SECONDS || undefined}
      // Read only where the animation is off, which is the one case with nothing running to take the width from.
      style={{ "--otp-remaining": remaining / period } as CSSProperties}
    >
      <Box
        className="otp-countdown-bar"
        style={{ "--otp-period": `${period}s`, "--otp-elapsed": `-${elapsed}s` } as CSSProperties}
      />
    </Box>
  );
}

interface CountdownProps {
  period: number;
  remaining: number;
}

// The one field two modes share: TOTP counts steps from it, and so does a suite that named a time step.
// The two icons are /time's own, since a box naming an instant is the same box wherever the seconds are counted into.
function TimeInput({ value, onChange, error, instant, live }: TimeInputProps) {
  return (
    <NumberInput
      label="Time"
      description="Epoch seconds, or the clock while it is empty"
      placeholder="Following the clock"
      value={value}
      onChange={onChange}
      min={0}
      max={MAX_TIME}
      allowDecimal={false}
      allowNegative={false}
      error={error}
      classNames={{ root: "relative-root", error: "absolute-error" }}
      // The two icons stand where the spinner did, a second either side of a step being nothing anybody steps to.
      rightSectionWidth={INSTANT_PICKER_WIDTH}
      rightSection={
        <InstantPicker
          instant={instant}
          live={live}
          onPick={(picked) => onChange(Math.floor(picked.getTime() / 1000))}
          onPin={() => onChange(Math.floor(Date.now() / 1000))}
          onClear={() => onChange("")}
        />
      }
    />
  );
}

// The text somebody is typing into the URI box, the URI the fields spell once it has been read back up into them,
// and what is wrong with it where it is not one of these URIs at all.
interface UriDraft {
  text: string;
  against: string;
  error: string | null;
}

interface TimeInputProps {
  value: number | string;
  onChange: (value: number | string) => void;
  error: string | null;
  instant: Date | null;
  live: boolean;
}

// Parsed on every keystroke, since the fields below it are the ones this suite asks for and nothing else.
function readSuite(text: string): { suite: Suite | null; error: string | null } {
  try {
    return { suite: parseSuite(text), error: null };
  } catch (error) {
    return { suite: null, error: error instanceof Error ? error.message : "This is not a suite" };
  }
}

const NO_SUITE = { suite: null, error: null };

// Where the bar changes colour, which is about as long as it takes to read a code and type it somewhere else.
const LOW_SECONDS = 5;

// Two subtle icons and the gap between them, which is what /time's own pair stands in.
const URI_ACTIONS_WIDTH = 64;
